import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Users, Search, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import clientAccountService from '../services/clientAccountService';
import alertService from '../services/alertService';
import { PageResponse } from '../types/api';
import { ClientAccountSummary } from '../types/clientAccount';

const formatCurrency = (v: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

const ClientAccountsPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState<ClientAccountSummary[]>([]);
  const [pageData, setPageData] = useState<PageResponse<ClientAccountSummary> | null>(null);
  const [search, setSearch] = useState('');
  const [documento, setDocumento] = useState('');
  const [page, setPage] = useState(0);
  const effectRanRef = useRef(false);

  const formatCpf = (value: string): string => {
    const v = (value || '').replace(/[^\d]/g, '').slice(0, 11);
    if (v.length <= 3) return v;
    if (v.length <= 6) return v.replace(/(\d{3})(\d+)/, '$1.$2');
    if (v.length <= 9) return v.replace(/(\d{3})(\d{3})(\d+)/, '$1.$2.$3');
    return v.replace(/(\d{3})(\d{3})(\d{3})(\d{0,2})/, '$1.$2.$3-$4');
  };

  const formatCnpj = (value: string): string => {
    const v = (value || '').replace(/[^\d]/g, '').slice(0, 14);
    if (v.length <= 2) return v;
    if (v.length <= 5) return v.replace(/(\d{2})(\d+)/, '$1.$2');
    if (v.length <= 8) return v.replace(/(\d{2})(\d{3})(\d+)/, '$1.$2.$3');
    if (v.length <= 12) return v.replace(/(\d{2})(\d{3})(\d{3})(\d+)/, '$1.$2.$3/$4');
    return v.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{0,2})/, '$1.$2.$3/$4-$5');
  };

  const documentoMask = useMemo(() => {
    const raw = documento.replace(/[^\d]/g, '');
    return raw.length > 11 ? formatCnpj(documento) : formatCpf(documento);
  }, [documento]);

  const load = async () => {
    try {
      setLoading(true);
      const raw = (documento || '').replace(/[^\d]/g, '');
      const isCnpj = raw.length > 11;
      const params: any = { page, size: 10, sort: ['nome'] };
      if (search) params.nome = search;
      if (raw) {
        if (isCnpj) params.cnpj = raw; else params.cpf = raw;
      }
      const res = await clientAccountService.listAccounts(params);
      const pageRes = res?.data as PageResponse<ClientAccountSummary>;
      const list = Array.isArray(pageRes?.content) ? pageRes.content : [];
      setAccounts(list);
      setPageData(pageRes || null);
    } catch (err: any) {
      alertService.error('Erro ao carregar contas de clientes', err?.message || 'Tente novamente');
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (effectRanRef.current) {
      load();
    }
  }, [page]);

  useEffect(() => {
    if (!effectRanRef.current) {
      load();
      effectRanRef.current = true;
    }
  }, []);

  const handleBuscar = () => { setPage(0); load(); };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Users className="h-6 w-6 text-red-600" />
          <div>
            <h1 className="text-xl font-bold text-gray-900">Contas de Clientes</h1>
            <p className="text-sm text-gray-600">Listagem de saldo devedor por cliente</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
        <div className="grid grid-cols-12 gap-4 items-end">
          <div className="col-span-12 md:col-span-5">
            <label className="block text-sm font-medium text-gray-700 mb-2">Documento</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input value={documentoMask} onChange={(e) => setDocumento(e.target.value)} placeholder="Digite CPF ou CNPJ" className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg" />
            </div>
          </div>
          <div className="col-span-12 md:col-span-5">
            <label className="block text-sm font-medium text-gray-700 mb-2">Nome (opcional)</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Filtrar por nome" className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg" />
            </div>
          </div>
          <div className="col-span-12 md:col-span-2">
            <button onClick={handleBuscar} className="w-full px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg">Buscar</button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Documento</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Saldo Devedor</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {accounts.map((c) => {
                const rawDoc = (c as any)?.cpf || '';
                const clean = rawDoc.replace(/[^\d]/g, '');
                const docFmt = clean.length > 11 ? formatCnpj(clean) : formatCpf(clean);
                return (
                  <tr key={c.clienteId}>
                    <td className="px-6 py-4 text-sm text-gray-900">{c.nomeCliente}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">{docFmt || '-'}</td>
                    <td className="px-6 py-4 text-sm text-gray-900 text-right">{formatCurrency(Number(c.saldoDevedor || 0))}</td>
                    <td className="px-6 py-4 text-sm text-right">
                      <button onClick={() => navigate(`/clientes/${c.clienteId}/conta`)} className="inline-flex items-center gap-1 px-3 py-1 border rounded">
                        <Eye className="h-4 w-4" /> Detalhes
                      </button>
                    </td>
                  </tr>
                );
              })}
              {!loading && accounts.length === 0 && (
                <tr>
                  <td className="px-6 py-8 text-center text-gray-500" colSpan={4}>Nenhum cliente encontrado</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mt-4">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setPage((prev) => Math.max(0, prev - 1))}
            disabled={!pageData || pageData.first || page <= 0}
            className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Anterior
          </button>
          <span className="text-sm text-gray-700">
            Página {(pageData ? pageData.number : page) + 1} de {pageData ? pageData.totalPages : 1}
          </span>
          <button
            onClick={() => setPage((prev) => prev + 1)}
            disabled={!pageData || pageData.last}
            className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Próximo
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClientAccountsPage;